import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Trophy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [login, setLogin] = useState({ email: "", password: "" });

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/dashboard");
  }, [user, navigate]);

  const doGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", login);
      setUser(data);
      toast.success(`Welcome back, ${data.name?.split(" ")[0] || "player"}!`);
      navigate(data.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-6 py-16" data-testid="login-page">
      <div className="absolute inset-0 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1655159428752-c700435e9983?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-navy/80" />
      </div>
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex flex-col items-center mb-8">
          <img src="/legends-logo.png?v=4" alt="Legends Poker" className="w-28 h-28 object-contain drop-shadow-[0_0_25px_rgba(212,175,55,0.25)]" />
        </Link>

        <div className="bg-navy-paper border border-slate-800 p-8">
          <Button onClick={doGoogle} variant="outline" className="w-full rounded-none border-slate-600 text-white hover:bg-white hover:text-navy py-6 mb-6" data-testid="google-login-btn">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5 mr-3" /> Continue with Google
          </Button>
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-white/10" /><span className="text-slate-500 text-xs uppercase">or</span><div className="flex-1 h-px bg-white/10" />
          </div>

          <h2 className="font-display text-2xl font-bold text-white mb-5">Log In</h2>
              <form onSubmit={doLogin} className="space-y-4">
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input required type="email" value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} className="mt-1.5 rounded-none bg-navy border-slate-700" data-testid="login-email" />
                </div>
                <div>
                  <Label className="text-slate-300">Password</Label>
                  <Input required type="password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} className="mt-1.5 rounded-none bg-navy border-slate-700" data-testid="login-password" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest py-6" data-testid="login-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log In"}
                </Button>
              </form>
          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-slate-400 text-sm">Not a member yet?</p>
            <Link to="/become-a-legend" className="inline-block mt-2 text-gold font-bold uppercase tracking-widest text-xs hover:text-gold-light underline" data-testid="login-become-legend-link">Become a Legend</Link>
          </div>
        </div>
        <p className="text-center text-slate-500 text-xs mt-6"><Link to="/" className="hover:text-gold">← Back to home</Link></p>
      </div>
    </div>
  );
}
